import { useEffect, useState } from "react";
import { X, ArrowUpRight } from "lucide-react";
import { toEmbedSrc, embedKind } from "@/lib/embeds";

/**
 * StickyPlayer — global fixed-bottom player triggered by a custom window event.
 *
 * How to trigger from anywhere:
 *   window.dispatchEvent(new CustomEvent("itzkatu:play", { detail: {
 *     url: "<embed or share url>",
 *     kind: "auto" | "audio" | "video" | "photo" | "product" | "link",
 *     title, subtitle,
 *     href                 // where "ver en instagram / go to shop / open" points
 *   }}))
 *
 * Behavior per kind:
 *   audio (Spotify/SC/Bandcamp) → sticky bottom bar with the embed player, close X.
 *   video (YouTube/Vimeo)       → small floating window bottom-right with close X.
 *   photo (Instagram)           → sticky bar with "ver en instagram" link (no iframe).
 *   product                     → sticky bar with "ver en la tienda" link.
 *   link / other                → sticky bar with generic external link.
 */
export default function StickyPlayer() {
    const [item, setItem] = useState(null);

    useEffect(() => {
        const onPlay = (ev) => setItem(ev.detail || null);
        const onEsc = (ev) => { if (ev.key === "Escape") setItem(null); };
        window.addEventListener("itzkatu:play", onPlay);
        window.addEventListener("keydown", onEsc);
        return () => {
            window.removeEventListener("itzkatu:play", onPlay);
            window.removeEventListener("keydown", onEsc);
        };
    }, []);

    if (!item) return null;
    const kind = (item.kind && item.kind !== "auto") ? item.kind : embedKind(item.url || "");
    const isAudio = kind === "audio";
    const isVideo = kind === "video";
    const isPhoto = kind === "photo" || kind === "vertical-video";
    const isProduct = kind === "product";
    const close = () => setItem(null);

    // ---- VIDEO: small floating window bottom-right ----
    if (isVideo && item.url) {
        return (
            <div
                data-testid="sticky-player-video"
                className="fixed bottom-4 right-4 z-[90] shadow-2xl bg-black w-[92vw] max-w-[420px]"
                style={{ aspectRatio: "16 / 9" }}
            >
                <iframe
                    title={item.title || "player"}
                    src={toEmbedSrc(item.url)}
                    className="block w-full h-full"
                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                    allowFullScreen
                />
                <button
                    onClick={close}
                    data-testid="sticky-player-close"
                    className="absolute -top-3 -right-3 bg-white text-black hover:bg-[#5D3FD3] hover:text-white p-1.5 shadow-lg transition-colors"
                    aria-label="close"
                >
                    <X size={14} />
                </button>
            </div>
        );
    }

    // ---- AUDIO: sticky bottom bar with embed player ----
    if (isAudio && item.url) {
        return (
            <div
                data-testid="sticky-player-audio"
                className="fixed inset-x-0 bottom-0 z-[90] bg-black text-white shadow-2xl"
            >
                <div className="flex items-center gap-3 px-3 md:px-4 py-2">
                    <div className="min-w-0 flex-1 flex flex-col md:flex-row md:items-baseline md:gap-3">
                        {item.title && <span className="font-heading font-black uppercase text-sm tracking-tight truncate">{item.title}</span>}
                        {item.subtitle && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 truncate">{item.subtitle}</span>}
                    </div>
                    <button
                        onClick={close}
                        data-testid="sticky-player-close"
                        className="shrink-0 p-1.5 hover:bg-[#5D3FD3] transition-colors"
                        aria-label="close"
                    >
                        <X size={14} />
                    </button>
                </div>
                <iframe
                    title={item.title || "player"}
                    src={toEmbedSrc(item.url)}
                    className="block w-full"
                    height={item.tall ? 232 : 152}
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                />
            </div>
        );
    }

    // ---- PHOTO (Instagram) / PRODUCT / OTHER LINK: sticky bar with CTA ----
    const ctaLabel = isPhoto
        ? "ver en instagram"
        : isProduct
        ? "ver en la tienda"
        : (item.cta_label || "open link");
    const ctaHref = item.href || item.url;

    return (
        <div
            data-testid="sticky-player-link"
            className="fixed inset-x-0 bottom-0 z-[90] bg-black text-white shadow-2xl px-3 md:px-4 py-3 flex items-center gap-3"
        >
            {item.thumbnail && (
                <img src={item.thumbnail} alt="" className="w-10 h-10 object-cover flex-none" referrerPolicy="no-referrer" />
            )}
            <div className="min-w-0 flex-1 flex flex-col">
                {item.title && <span className="font-heading font-black uppercase text-sm tracking-tight truncate">{item.title}</span>}
                {item.subtitle && <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 truncate">{item.subtitle}</span>}
            </div>
            {ctaHref && (
                <a
                    href={ctaHref}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="sticky-player-cta"
                    className="shrink-0 inline-flex items-center gap-1.5 bg-white text-black hover:bg-[#5D3FD3] hover:text-white transition-colors font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2"
                >
                    {ctaLabel} <ArrowUpRight size={11} />
                </a>
            )}
            <button
                onClick={close}
                data-testid="sticky-player-close"
                className="shrink-0 p-1.5 hover:bg-[#5D3FD3] transition-colors"
                aria-label="close"
            >
                <X size={14} />
            </button>
        </div>
    );
}
